import { useEffect, useState } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { formatEntityTypeLabel } from "@/components/missions/entityTypeDisplay";
import { useOsNavigate } from "@/components/ui/os-layout";
import type { Alert } from "@/services/api/guardiansv2/types";
import { ContextRow } from "../shared/ContextRow";
import { EntityLink } from "../shared/EntityLink";
import { guardiansShareUrl } from "@/navigation/apps/guardiansLocation";
import { alertEntityLabel } from "./alertTimelineLink";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";

export interface AlertInspectorContextProps {
  alert: Alert;
  scopeLabels: ScopeLabelCache;
}

/** Entity and scope sidebar for the alert detail panel. */
export function AlertInspectorContext({
  alert,
  scopeLabels,
}: AlertInspectorContextProps) {
  const osNavigate = useOsNavigate();
  const { scope, subject } = alert;

  const [clientName, setClientName] = useState<string | null>(null);
  const [clientLoading, setClientLoading] = useState(false);

  const missionId = scope.mission_id;
  const taskId = scope.task_id;
  const interactionId = scope.interaction_id;
  const entityType = scope.entity_type;
  const entityId = scope.entity_id;

  useEffect(() => {
    if (entityType !== "fa_client" || !entityId) {
      setClientName(null);
      return;
    }
    let mounted = true;
    setClientLoading(true);
    void AgenticV2API.getClient(entityId)
      .then((client) => {
        if (mounted) setClientName(client.display_name || null);
      })
      .catch(() => {
        if (mounted) setClientName(null);
      })
      .finally(() => {
        if (mounted) setClientLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [entityType, entityId]);

  const clientTypeLabel = entityType ? formatEntityTypeLabel(entityType) : "";
  const clientText =
    clientName ??
    (clientLoading
      ? "Loading…"
      : entityId
        ? clientTypeLabel || "Client"
        : null);

  const entityLabel = alertEntityLabel(alert);

  const auditHref = alert.audit_id
    ? guardiansShareUrl({ view: "audits", audit: alert.audit_id })
    : null;

  return (
    <section className="space-y-3" data-testid="alert-inspector-context">
      <ContextRow label="Entity type">
        <span className="capitalize">{entityLabel}</span>
      </ContextRow>
      <ContextRow label="Subject" data-testid="alert-context-subject">
        <EntityLink subject={subject} scope={scope} scopeLabels={scopeLabels} />
      </ContextRow>
      {subject.version != null ? (
        <ContextRow label="Version">{subject.version}</ContextRow>
      ) : null}
      {missionId ? (
        <ContextRow label="Mission" data-testid="alert-context-mission">
          <EntityLink
            subject={{ type: "mission", id: missionId }}
            scope={{ mission_id: missionId }}
            scopeLabels={scopeLabels}
          />
        </ContextRow>
      ) : null}
      {taskId ? (
        <ContextRow label="Task" data-testid="alert-context-task">
          <EntityLink
            subject={{ type: "task", id: taskId }}
            scope={{ mission_id: missionId, task_id: taskId }}
            scopeLabels={scopeLabels}
          />
        </ContextRow>
      ) : null}
      {interactionId ? (
        <ContextRow label="Interaction" data-testid="alert-context-interaction">
          <EntityLink
            subject={{ type: "interaction", id: interactionId }}
            scope={{
              mission_id: missionId,
              task_id: taskId,
              interaction_id: interactionId,
            }}
            scopeLabels={scopeLabels}
          />
        </ContextRow>
      ) : null}
      {entityType && entityId && clientText ? (
        <ContextRow
          label={clientTypeLabel || "Client"}
          onClick={
            entityType === "fa_client"
              ? () => osNavigate(`/clients/${entityId}`)
              : undefined
          }
          data-testid="alert-context-client"
        >
          {clientText}
        </ContextRow>
      ) : null}
      {alert.audit_id && auditHref ? (
        <ContextRow
          label="Linked audit"
          onClick={() => osNavigate(auditHref)}
          data-testid="alert-context-linked-audit"
        >
          View audit
        </ContextRow>
      ) : null}
    </section>
  );
}
